import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useBootstrap, useTasaDia } from '../api/queries';
import {
  useAnularFactura,
  useAnularNota,
  useCrearFactura,
  useCrearNota,
  useEmitirFiscal,
  useEmitirFiscalNota,
  useFacturaDetalle,
  useFacturasFinanzas,
  useNotasList,
} from '../api/queries-finanzas';
import { descargarArchivo } from '../api/client';
import { FinanceDemoNotice, TabBar } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { SearchSelect } from '../components/SearchSelect';
import { MotivoModal } from '../components/MotivoModal';
import { StatusBadge } from '../components/Badges';
import { SortableTh } from '../components/SortableTh';
import { PlusIcon, TrashIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { addDaysIso, formatDate, formatMoney, formatRate, todayIso } from '../lib/format';
import { useTableSort } from '../lib/useTableSort';
import type { Factura, FacturaDetalle, FacturaItem, NotaModalidad } from '../api/types';

interface LineaForm {
  productoId: string;
  descripcion: string;
  cantidad: string;
  precio: string;
  exenta: boolean;
}

const today = todayIso();
const in30Days = addDaysIso(30);

const EMPTY_LINE: LineaForm = {
  productoId: '',
  descripcion: '',
  cantidad: '1',
  precio: '',
  exenta: false,
};

export function FacturasPage() {
  const { user, demo } = useAuth();
  const { notify } = useToast();
  const { data: bootstrap } = useBootstrap();
  const [estado, setEstado] = useState('');
  const [search, setSearch] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [openForm, setOpenForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notaForm, setNotaForm] = useState<{ tipo: 'CREDITO' | 'DEBITO'; factura: FacturaDetalle } | null>(null);
  const [view, setView] = useState<'facturas' | 'notas'>('facturas');
  const [notaFiltro, setNotaFiltro] = useState<'' | 'CREDITO' | 'DEBITO'>('');
  const [notaDesde, setNotaDesde] = useState('');
  const [notaHasta, setNotaHasta] = useState('');
  const [notaPickOpen, setNotaPickOpen] = useState(false);
  const [notaPickSearch, setNotaPickSearch] = useState('');
  const [notaPick, setNotaPick] = useState<{ tipo: 'CREDITO' | 'DEBITO'; facturaId: string } | null>(null);
  const [annulFactura, setAnnulFactura] = useState<Factura | null>(null);
  const [annulNota, setAnnulNota] = useState<{ id: string; numero: string } | null>(null);

  const facturas = useFacturasFinanzas({
    estado: estado || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    search: search || undefined,
    page,
    limit: 25,
  });
  const detalle = useFacturaDetalle(selectedId);
  const crear = useCrearFactura();
  const anular = useAnularFactura();
  const notas = useNotasList({
    tipo: notaFiltro || undefined,
    desde: notaDesde || undefined,
    hasta: notaHasta || undefined,
    limit: 200,
  });
  const notaPickFacturas = useFacturasFinanzas({
    search: notaPickSearch || undefined,
    page: 1,
    limit: 20,
  });
  const notaPickDetalle = useFacturaDetalle(notaPick?.facturaId ?? null);
  const emitirNotaFiscal = useEmitirFiscalNota();
  const anularNotaFila = useAnularNota();

  const rows = facturas.data?.data ?? [];
  const { rows: sortedRows, sort, toggleSort } = useTableSort<
    Factura,
    'documento' | 'cliente' | 'fecha' | 'base' | 'iva' | 'total' | 'saldo' | 'estado'
  >(
    rows,
    {
      documento: (row) => row.numero,
      cliente: (row) => row.clienteNombre,
      fecha: (row) => row.fechaEmision,
      base: (row) => row.subtotalVes,
      iva: (row) => row.impuestoVes,
      total: (row) => row.totalVes,
      saldo: (row) => row.saldoVes,
      estado: (row) => row.estado,
    },
    'fecha',
    'desc',
  );
  const totalFacturado = rows.reduce((sum, row) => sum + row.totalVes, 0);
  const totalSaldo = rows.reduce((sum, row) => sum + row.saldoVes, 0);
  const canCreate = !demo && user !== null && ['admin', 'supervisor', 'ventas'].includes(user.rol);
  const canAnnul = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);

  const handleAnnul = (factura: Factura) => setAnnulFactura(factura);

  const confirmarAnulacionFactura = async (motivo: string) => {
    if (!annulFactura) return;
    await anular.mutateAsync({ id: annulFactura.id, motivo });
    notify(`Factura ${annulFactura.numero} anulada con asiento de reverso.`, 'success');
    setAnnulFactura(null);
    setSelectedId(null);
  };

  const confirmarAnulacionNota = async (motivo: string) => {
    if (!annulNota) return;
    await anularNotaFila.mutateAsync({ id: annulNota.id, motivo });
    notify(`Nota ${annulNota.numero} anulada con asiento de reverso.`, 'success');
    setAnnulNota(null);
  };

  const emitirNotaDesdeLista = async (id: string, numero: string) => {
    try {
      const result = await emitirNotaFiscal.mutateAsync(id);
      notify(`Nota ${numero} emitida con control ${result.data.numeroControl}.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo emitir la nota.', 'error');
    }
  };

  const renderNotas = () => (
    <section className="card transactions-card">
      <div className="transactions-filters">
        <div className="filter-controls">
          <select
            className="input"
            value={notaFiltro}
            onChange={(event) => setNotaFiltro(event.target.value as typeof notaFiltro)}
            aria-label="Tipo de nota"
          >
            <option value="">Todas las notas</option>
            <option value="CREDITO">Notas de crédito</option>
            <option value="DEBITO">Notas de débito</option>
          </select>
          <input type="date" className="input input--date" value={notaDesde} onChange={(e) => setNotaDesde(e.target.value)} aria-label="Desde" />
          <input type="date" className="input input--date" value={notaHasta} onChange={(e) => setNotaHasta(e.target.value)} aria-label="Hasta" />
          <button type="button" className="btn btn--primary btn--sm" onClick={() => setNotaPickOpen(true)} disabled={!canCreate}>
            <PlusIcon size={15} /> Nueva nota
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="data-table data-table--compact">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Nota</th>
              <th>Factura</th>
              <th>Cliente</th>
              <th className="align-right">Total Bs.</th>
              <th>Estado</th>
              <th>Fiscal</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {notas.isLoading ? (
              <tr><td colSpan={8}><div className="table-empty">Cargando notas…</div></td></tr>
            ) : null}
            {!notas.isLoading && (notas.data?.length ?? 0) === 0 ? (
              <tr><td colSpan={8}><div className="table-empty">Sin notas en el período.</div></td></tr>
            ) : null}
            {(notas.data ?? []).map((nota) => (
              <tr key={nota.id}>
                <td>{formatDate(nota.fecha)}</td>
                <td>
                  <div className="doc-cell">
                    <strong>{nota.numero}</strong>
                    <span>
                      {nota.tipo === 'CREDITO' ? 'Crédito' : 'Débito'}
                      {nota.modalidad === 'AJUSTE_PRECIO'
                        ? ' · ajuste de precio'
                        : nota.modalidad === 'DEVOLUCION'
                          ? ' · devolución'
                          : ' · cargo'}
                    </span>
                  </div>
                </td>
                <td>{nota.facturaNumero}</td>
                <td>{nota.clienteNombre}</td>
                <td className="align-right">{formatMoney(nota.totalVes, 'VES')}</td>
                <td><StatusBadge estado={nota.estado} /></td>
                <td>
                  <div className="doc-cell">
                    <strong>{nota.fiscalEstado ?? 'NO_EMITIDA'}</strong>
                    <span>{nota.numeroControl ?? 'Sin control'}</span>
                  </div>
                </td>
                <td>
                  <div className="row-actions">
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSelectedId(nota.facturaId)}>
                      Factura
                    </button>
                    {nota.fiscalEstado === 'NO_EMITIDA' && canCreate ? (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void emitirNotaDesdeLista(nota.id, nota.numero)} disabled={emitirNotaFiscal.isPending}>
                        Fiscal
                      </button>
                    ) : null}
                    {nota.fiscalUrl ? (
                      <a className="btn btn--ghost btn--sm" href={nota.fiscalUrl} target="_blank" rel="noopener noreferrer">
                        Ver
                      </a>
                    ) : null}
                    {nota.estado === 'emitida' && canCreate ? (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setAnnulNota({ id: nota.id, numero: nota.numero })}>
                        Anular
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <footer className="table-footer">
        <span>{notas.data?.length ?? 0} notas · período {notaDesde || 'inicio'} → {notaHasta || 'hoy'}</span>
      </footer>
    </section>
  );

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Ventas y facturación</p>
          <h1 className="page-title">Facturas de Venta</h1>
          <p className="page-sub">
            Montos en bolívares, respaldo en USD, IVA por línea, correlativo fiscal y contabilización automática.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setOpenForm(true)}
          disabled={!canCreate}
        >
          <PlusIcon size={17} />
          Nueva factura
        </button>
      </header>

      <FinanceDemoNotice demo={demo} />

      <div className="page-tabs-row">
        <TabBar
          label="Vista de facturación"
          value={view}
          onChange={(value) => setView(value)}
          tabs={[
            { value: 'facturas', label: 'Facturas' },
            { value: 'notas', label: 'Notas de crédito / débito' },
          ]}
        />
      </div>

      {view === 'notas' ? (
        renderNotas()
      ) : (
        <>
      <div className="summary-strip">
        <div className="summary-tile">
          <span>Facturado (página)</span>
          <strong>{formatMoney(totalFacturado, 'VES')}</strong>
          <small>{rows.length} documentos</small>
        </div>
        <div className="summary-tile summary-tile--warning">
          <span>Saldo por cobrar</span>
          <strong>{formatMoney(totalSaldo, 'VES')}</strong>
          <small>Facturas emitidas y parciales</small>
        </div>
        <div className="summary-tile summary-tile--accent">
          <span>Tasa aplicada</span>
          <strong>{bootstrap?.tasas.find((t) => t.moneda === 'USD')?.tasa.toFixed(2) ?? '—'}</strong>
          <small>VES por USD</small>
        </div>
      </div>

      <section className="card transactions-card">
        <div className="transactions-filters">
          <div className="filter-tabs">
            {[
              ['', 'Todas'],
              ['emitida', 'Emitidas'],
              ['parcial', 'Parciales'],
              ['pagada', 'Pagadas'],
              ['anulada', 'Anuladas'],
            ].map(([value, label]) => (
              <button
                key={value || 'todas'}
                type="button"
                className={estado === value ? 'is-active' : undefined}
                onClick={() => {
                  setEstado(value ?? '');
                  setPage(1);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="filter-controls">
            <input
              type="date"
              className="input input--date"
              value={desde}
              onChange={(event) => setDesde(event.target.value)}
              aria-label="Desde"
            />
            <input
              type="date"
              className="input input--date"
              value={hasta}
              onChange={(event) => setHasta(event.target.value)}
              aria-label="Hasta"
            />
            <div className="search-input search-input--sm">
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Número, cliente o RIF"
                aria-label="Buscar facturas"
              />
            </div>
          </div>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Documento" sortKey="documento" sort={sort} onSort={toggleSort} />
                <SortableTh label="Cliente / RIF" sortKey="cliente" sort={sort} onSort={toggleSort} />
                <SortableTh label="Fecha" sortKey="fecha" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Base" sortKey="base" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="IVA" sortKey="iva" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Total" sortKey="total" sort={sort} onSort={toggleSort} />
                <SortableTh align="right" label="Saldo" sortKey="saldo" sort={sort} onSort={toggleSort} />
                <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {facturas.isLoading ? (
                <tr><td colSpan={8}><div className="table-empty">Cargando facturas…</div></td></tr>
              ) : null}
              {!facturas.isLoading && rows.length === 0 ? (
                <tr><td colSpan={8}><div className="table-empty">Sin facturas en este rango.</div></td></tr>
              ) : null}
              {sortedRows.map((row) => (
                <tr key={row.id} className="row-clickable" onClick={() => setSelectedId(row.id)}>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.numero}</strong>
                      <span>{row.numeroControl ? `Control ${row.numeroControl}` : row.tipo}</span>
                    </div>
                  </td>
                  <td>
                    <div className="doc-cell">
                      <strong>{row.clienteNombre}</strong>
                      <span>{row.clienteRif}</span>
                    </div>
                  </td>
                  <td>{formatDate(row.fechaEmision)}</td>
                  <td className="align-right">{formatMoney(row.subtotalVes, 'VES')}</td>
                  <td className="align-right">{formatMoney(row.impuestoVes, 'VES')}</td>
                  <td className="align-right">
                    <div className="amount-cell">
                      <strong>{formatMoney(row.totalVes, 'VES')}</strong>
                    </div>
                  </td>
                  <td className="align-right">
                    <span className={row.saldoUsd > 0 ? 'saldo-pending' : 'saldo-ok'}>
                      {formatMoney(row.saldoVes, 'VES')}
                    </span>
                  </td>
                  <td><StatusBadge estado={row.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>
            Mostrando {rows.length ? (page - 1) * 25 + 1 : 0}–
            {Math.min(page * 25, facturas.data?.meta.total ?? rows.length)} de{' '}
            {facturas.data?.meta.total ?? rows.length}
          </span>
          <div className="pager">
            <button
              type="button"
              className="btn btn--outline btn--sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Anterior
            </button>
            <span className="pager__current">{page} / {facturas.data?.meta.pages ?? 1}</span>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              disabled={page >= (facturas.data?.meta.pages ?? 1)}
              onClick={() => setPage((current) => current + 1)}
            >
              Siguiente
            </button>
          </div>
        </footer>
      </section>
        </>
      )}

      {openForm ? (
        <FacturaFormModal
          onClose={() => setOpenForm(false)}
          onCreated={(numero) => {
            setOpenForm(false);
            notify(`Factura ${numero} generada y contabilizada.`, 'success');
          }}
        />
      ) : null}

      {notaPickOpen ? (
        <Modal
          title="Nueva nota de crédito o débito"
          eyebrow="Selecciona la factura de venta"
          onClose={() => setNotaPickOpen(false)}
          wide
        >
          <div className="modal-card__body">
            <div className="search-input">
              <input
                type="search"
                value={notaPickSearch}
                onChange={(event) => setNotaPickSearch(event.target.value)}
                placeholder="Buscar factura, cliente o RIF"
                aria-label="Buscar factura para la nota"
              />
            </div>
            <div className="simple-list">
              {(notaPickFacturas.data?.data ?? []).map((factura) => (
                <li key={factura.id}>
                  <span>
                    {factura.numero} · {factura.clienteNombre} · {formatDate(factura.fechaEmision)}
                  </span>
                  <div className="row-actions">
                    <strong>{formatMoney(factura.totalVes, 'VES')}</strong>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setNotaPick({ tipo: 'CREDITO', facturaId: factura.id });
                        setNotaPickOpen(false);
                      }}
                    >
                      Crédito
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setNotaPick({ tipo: 'DEBITO', facturaId: factura.id });
                        setNotaPickOpen(false);
                      }}
                    >
                      Débito
                    </button>
                  </div>
                </li>
              ))}
            </div>
            {notaPickFacturas.isLoading ? <p className="muted">Cargando facturas…</p> : null}
            {!notaPickFacturas.isLoading && (notaPickFacturas.data?.data.length ?? 0) === 0 ? (
              <p className="muted">No hay facturas que coincidan.</p>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {notaPick && notaPickDetalle.data ? (
        <NotaFormModal
          tipo={notaPick.tipo}
          factura={notaPickDetalle.data}
          onClose={() => setNotaPick(null)}
          onCreated={() => setNotaPick(null)}
        />
      ) : null}

      {notaForm ? (
        <NotaFormModal
          tipo={notaForm.tipo}
          factura={notaForm.factura}
          onClose={() => setNotaForm(null)}
          onCreated={() => setNotaForm(null)}
        />
      ) : null}

      {annulFactura ? (
        <MotivoModal
          title={`Anular factura ${annulFactura.numero}`}
          description="Se reversará la CxC, el IVA y los ingresos; si la factura movió inventario, también se reversa."
          onClose={() => setAnnulFactura(null)}
          onConfirm={confirmarAnulacionFactura}
        />
      ) : null}

      {annulNota ? (
        <MotivoModal
          title={`Anular nota ${annulNota.numero}`}
          description="Se reversará el asiento de la nota y el saldo del documento."
          onClose={() => setAnnulNota(null)}
          onConfirm={confirmarAnulacionNota}
        />
      ) : null}

      {selectedId && detalle.data ? (
        <DetalleFacturaModal
          factura={detalle.data}
          canAnnul={canAnnul}
          onClose={() => setSelectedId(null)}
          onAnnul={() => void handleAnnul(detalle.data)}
          onNota={(tipo) => {
            setNotaForm({ tipo, factura: detalle.data });
            setSelectedId(null);
          }}
          onAnularNota={setAnnulNota}
        />
      ) : null}
    </div>
  );
}

function FacturaFormModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (numero: string) => void;
}) {
  const { data: bootstrap } = useBootstrap();
  const { usdRate } = useCurrency();
  const { notify } = useToast();
  const crear = useCrearFactura();
  const [clienteId, setClienteId] = useState('');
  const [tipo, setTipo] = useState<'SERVICIO' | 'ARTICULO'>('ARTICULO');
  const [fechaEmision, setFechaEmision] = useState(today);
  const [fechaVencimiento, setFechaVencimiento] = useState(in30Days);
  const [tasa, setTasa] = useState(
    String(bootstrap?.tasas.find((t) => t.moneda === 'USD')?.tasa ?? 36.42),
  );
  const [observaciones, setObservaciones] = useState('');
  const [lineas, setLineas] = useState<LineaForm[]>([{ ...EMPTY_LINE }]);
  const tasaDia = useTasaDia(fechaEmision);

  useEffect(() => {
    const rate = tasaDia.data?.data.find((item) => item.moneda === 'USD');
    if (rate) setTasa(String(rate.tasa));
  }, [tasaDia.data]);

  const productos = bootstrap?.productos ?? [];
  const clientes = bootstrap?.clientes ?? [];

  const updateLine = (index: number, patch: Partial<LineaForm>) => {
    setLineas((current) =>
      current.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );
  };

  const selectProduct = (index: number, productoId: string) => {
    const producto = productos.find((p) => p.id === productoId);
    updateLine(index, {
      productoId,
      descripcion: producto?.nombre ?? '',
      precio: producto
        ? String(Math.round(producto.precioUsd * (Number(tasa) || usdRate) * 100) / 100)
        : '',
      exenta: producto ? producto.impuestoPct === 0 : false,
    });
  };

  const totals = useMemo(() => {
    let base = 0;
    let tax = 0;
    for (const line of lineas) {
      const qty = Number(line.cantidad) || 0;
      const price = Number(line.precio) || 0;
      const subtotal = qty * price;
      base += subtotal;
      if (!line.exenta) tax += subtotal * 0.16;
    }
    return {
      base: Math.round(base * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round((base + tax) * 100) / 100,
    };
  }, [lineas]);

  const submit = async () => {
    if (!clienteId) {
      notify('Selecciona un cliente.', 'warning');
      return;
    }
    const items = lineas
      .filter((line) => line.productoId && Number(line.cantidad) > 0)
      .map((line) => ({
        productoId: line.productoId,
        descripcion: line.descripcion || 'Ítem',
        cantidad: Number(line.cantidad),
        ...(line.precio
          ? { precioUnitarioUsd: Number(line.precio) / (Number(tasa) || usdRate || 1) }
          : {}),
        exenta: line.exenta,
        impuestoPct: line.exenta ? 0 : 16,
      }));
    if (items.length === 0) {
      notify('Agrega al menos un ítem con producto y cantidad.', 'warning');
      return;
    }

    try {
      const response = await crear.mutateAsync({
        clienteId,
        tipo,
        fechaEmision,
        fechaVencimiento,
        tasa: Number(tasa),
        ...(observaciones ? { observaciones } : {}),
        idempotencyKey: crypto.randomUUID(),
        items,
      });
      onCreated(response.data.numero);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la factura.', 'error');
    }
  };

  return (
    <Modal
      title="Nueva factura de venta"
      eyebrow="Facturación"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={crear.isPending}
          >
            {crear.isPending ? 'Emitiendo…' : 'Emitir y contabilizar'}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Cliente *</span>
            <SearchSelect
              value={clienteId}
              onChange={setClienteId}
              options={clientes.map((cliente) => ({
                value: cliente.id,
                label: cliente.nombre,
                hint: cliente.rif,
              }))}
              placeholder="Buscar cliente por nombre o RIF"
              ariaLabel="Cliente"
            />
          </label>
          <label className="field">
            <span className="field-label">Tipo</span>
            <select
              className="input"
              value={tipo}
              onChange={(event) => setTipo(event.target.value as 'SERVICIO' | 'ARTICULO')}
            >
              <option value="ARTICULO">Artículos</option>
              <option value="SERVICIO">Servicios</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">Tasa VES/USD</span>
            <input
              className="input"
              inputMode="decimal"
              value={tasa}
              onChange={(event) => setTasa(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Fecha emisión</span>
            <input
              className="input"
              type="date"
              value={fechaEmision}
              onChange={(event) => setFechaEmision(event.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Vencimiento</span>
            <input
              className="input"
              type="date"
              value={fechaVencimiento}
              onChange={(event) => setFechaVencimiento(event.target.value)}
            />
          </label>
        </div>

        <p className="inline-note">
          El número de control fiscal se asigna automáticamente desde
          <strong> Contabilidad → Numeración fiscal</strong>.
        </p>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Ítems de la factura</strong>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setLineas((current) => [...current, { ...EMPTY_LINE }])}
            >
              <PlusIcon size={15} /> Agregar línea
            </button>
          </div>
          {lineas.map((line, index) => (
            <div className="item-row" key={index}>
              <SearchSelect
                value={line.productoId}
                onChange={(value) => selectProduct(index, value)}
                options={productos.map((producto) => ({
                  value: producto.id,
                  label: producto.nombre,
                  hint: `${producto.sku} · ${producto.tipo === 'ARTICULO' ? 'Artículo' : 'Servicio'}`,
                }))}
                placeholder="Producto / servicio…"
                ariaLabel="Producto"
                className="item-row__product"
              />
              <input
                className="input item-row__qty"
                type="number"
                min="0.01"
                step="0.01"
                value={line.cantidad}
                onChange={(event) => updateLine(index, { cantidad: event.target.value })}
                aria-label="Cantidad"
              />
              <input
                className="input item-row__price"
                type="number"
                min="0"
                step="0.01"
                value={line.precio}
                onChange={(event) => updateLine(index, { precio: event.target.value })}
                placeholder="Precio Bs."
                aria-label="Precio Bs."
              />
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={line.exenta}
                  onChange={(event) => updateLine(index, { exenta: event.target.checked })}
                />
                Exenta
              </label>
              <button
                type="button"
                className="icon-button icon-button--tiny"
                onClick={() =>
                  setLineas((current) =>
                    current.length > 1 ? current.filter((_, i) => i !== index) : current,
                  )
                }
                aria-label="Eliminar línea"
              >
                <TrashIcon size={16} />
              </button>
            </div>
          ))}
          <div className="line-items__totals">
            <span>Base {formatMoney(totals.base, 'VES')}</span>
            <span>IVA {formatMoney(totals.tax, 'VES')}</span>
            <strong>Total {formatMoney(totals.total, 'VES')}</strong>
          </div>
        </div>

        <label className="field">
          <span className="field-label">Observaciones</span>
          <textarea
            className="textarea"
            rows={2}
            value={observaciones}
            onChange={(event) => setObservaciones(event.target.value)}
          />
        </label>
        <p className="page-footnote">
          Al emitir se reserva el correlativo FAC, se descuenta inventario si aplica y se
          genera el asiento CxC / ingresos / IVA en la misma transacción.
        </p>
      </div>
    </Modal>
  );
}

function DetalleFacturaModal({
  factura,
  canAnnul,
  onClose,
  onAnnul,
  onNota,
  onAnularNota,
}: {
  factura: FacturaDetalle;
  canAnnul: boolean;
  onClose: () => void;
  onAnnul: () => void;
  onNota: (tipo: 'CREDITO' | 'DEBITO') => void;
  onAnularNota: (nota: { id: string; numero: string }) => void;
}) {
  const { notify } = useToast();
  const emitirFiscal = useEmitirFiscal();
  const emitirFiscalNota = useEmitirFiscalNota();
  const [fiscalBusy, setFiscalBusy] = useState(false);
  const [notaFiscalId, setNotaFiscalId] = useState<string | null>(null);

  const descargarXml = async () => {
    try {
      const blob = await descargarArchivo(`/api/facturas/${factura.id}/fiscal.xml`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${factura.numero}-fiscal.xml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo descargar el XML.', 'error');
    }
  };

  const emitirNota = async (notaId: string) => {
    setNotaFiscalId(notaId);
    try {
      const result = await emitirFiscalNota.mutateAsync(notaId);
      notify(`Nota emitida con control ${result.data.numeroControl}.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo emitir la nota.', 'error');
    } finally {
      setNotaFiscalId(null);
    }
  };

  const descargarNotaXml = async (notaId: string, numero: string) => {
    try {
      const blob = await descargarArchivo(`/api/notas/${notaId}/fiscal.xml`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${numero}-fiscal.xml`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo descargar el XML.', 'error');
    }
  };

  const emitir = async () => {
    setFiscalBusy(true);
    try {
      const result = await emitirFiscal.mutateAsync({ id: factura.id });
      notify(
        `Factura emitida en ${result.data.proveedor} · control ${result.data.numeroControl}.`,
        'success',
      );
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo emitir fiscalmente.', 'error');
    } finally {
      setFiscalBusy(false);
    }
  };

  return (
    <Modal
      title={factura.numero}
      eyebrow="Detalle de factura"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cerrar
          </button>
          {canAnnul && factura.estado !== 'anulada' ? (
            <>
              <button type="button" className="btn btn--outline" onClick={() => onNota('CREDITO')}>
                Nota de crédito
              </button>
              <button type="button" className="btn btn--outline" onClick={() => onNota('DEBITO')}>
                Nota de débito
              </button>
              <button type="button" className="btn btn--danger" onClick={onAnnul}>
                Anular con reverso
              </button>
            </>
          ) : null}
        </>
      }
    >
      <div className="modal-card__body">
        <div className="detail-grid">
          <div className="detail-block">
            <span>Cliente</span>
            <strong>{factura.clienteNombre}</strong>
            <small>{factura.clienteRif}</small>
          </div>
          <div className="detail-block">
            <span>Estado</span>
            <StatusBadge estado={factura.estado} />
            <small>Saldo {formatMoney(factura.saldoVes, 'VES')}</small>
          </div>
          <div className="detail-block">
            <span>Emisión</span>
            <strong>{formatDate(factura.fechaEmision)}</strong>
            <small>Vence {factura.fechaVencimiento ?? '—'}</small>
          </div>
          <div className="detail-block">
            <span>Asiento</span>
            <strong>{factura.asiento ? `#${factura.asiento.numero}` : 'Sin asiento'}</strong>
            <small>{factura.asiento?.estado ?? '—'}</small>
          </div>
        </div>

        <div className="table-scroll">
          <table className="data-table data-table--compact">
            <thead>
              <tr>
                <th>Descripción</th>
                <th className="align-right">Cant.</th>
                <th className="align-right">Precio</th>
                <th className="align-right">IVA</th>
                <th className="align-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {factura.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="doc-cell">
                      <strong>{item.descripcion}</strong>
                      <span>{item.cuentaCodigo ? `${item.cuentaCodigo} · ${item.cuentaNombre}` : item.exenta ? 'Exenta' : 'Gravada'}</span>
                    </div>
                  </td>
                  <td className="align-right">{item.cantidad}</td>
                  <td className="align-right">{formatMoney(item.precioUnitarioVes > 0 ? item.precioUnitarioVes : item.precioUnitarioUsd * factura.tasa, 'VES')}</td>
                  <td className="align-right">{formatMoney(item.impuestoUsd * factura.tasa, 'VES')}</td>
                  <td className="align-right">{formatMoney(item.totalUsd * factura.tasa, 'VES')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <dl className="detail-total">
          <div><dt>Base</dt><dd>{formatMoney(factura.subtotalVes, 'VES')}</dd></div>
          <div><dt>IVA</dt><dd>{formatMoney(factura.impuestoVes, 'VES')}</dd></div>
          <div><dt>Total Bs.</dt><dd>{formatMoney(factura.totalVes, 'VES')}</dd></div>
        </dl>

        {factura.pagos.length > 0 ? (
          <div>
            <h3 className="card-title">Cobros aplicados</h3>
            <ul className="simple-list">
              {factura.pagos.map((pago) => (
                <li key={pago.id}>
                  <span>{pago.numero} · {formatDate(pago.cobradoAt)}</span>
                  <strong>
                    {formatMoney(pago.montoAplicadoUsd * factura.tasa, 'VES')}
                    {pago.retencionIslrUsd > 0
                      ? ` + ISLR ${formatMoney(pago.retencionIslrUsd * factura.tasa, 'VES')}`
                      : ''}
                  </strong>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="detail-grid">
          <div className="detail-block">
            <span>Estado fiscal</span>
            <strong>{factura.fiscal.estado.replace('_', ' ')}</strong>
            <small>
              {factura.numeroControl ? `Control ${factura.numeroControl}` : 'Sin número de control'}
              {factura.fiscal.proveedor ? ` · ${factura.fiscal.proveedor}` : ''}
            </small>
            {factura.fiscal.hash ? <small>Hash {factura.fiscal.hash.slice(0, 16)}…</small> : null}
          </div>
          <div className="detail-block">
            <span>Documento fiscal</span>
            {canAnnul && factura.fiscal.estado === 'NO_EMITIDA' ? (
              <button type="button" className="btn btn--outline btn--sm" onClick={() => void emitir()} disabled={fiscalBusy}>
                {fiscalBusy ? 'Emitiendo…' : 'Emitir fiscal'}
              </button>
            ) : null}
            {factura.fiscal.estado !== 'NO_EMITIDA' && factura.fiscal.url ? (
              <div className="detail-block__actions">
                <a
                  className="btn btn--primary btn--sm"
                  href={factura.fiscal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver factura
                </a>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => void descargarXml()}
                >
                  XML
                </button>
              </div>
            ) : null}
            {factura.fiscal.estado !== 'NO_EMITIDA' && !factura.fiscal.url ? (
              <button type="button" className="btn btn--outline btn--sm" onClick={() => void descargarXml()}>
                Descargar XML
              </button>
            ) : null}
          </div>
        </div>

        {factura.notas.length > 0 ? (
          <div>
            <h3 className="card-title">Notas de crédito / débito</h3>
            <ul className="simple-list">
              {factura.notas.map((nota) => (
                <li key={nota.id}>
                  <span>
                    {nota.tipo === 'CREDITO' ? 'Crédito' : 'Débito'}
                    {nota.modalidad === 'AJUSTE_PRECIO'
                      ? ' · ajuste de precio'
                      : nota.modalidad === 'DEVOLUCION'
                        ? ' · devolución'
                        : ' · cargo'}
                    {' '}{nota.numero} · {formatDate(nota.fecha)} · {nota.estado}
                    {nota.numeroControl ? ` · control ${nota.numeroControl}` : ''}
                  </span>
                  <div className="row-actions">
                    <strong>
                      {formatMoney(nota.totalVes, 'VES')}
                      {nota.saldoFavorUsd > 0
                        ? ` · favor ${formatMoney(nota.saldoFavorUsd, 'USD')}`
                        : ''}
                    </strong>
                    {canAnnul && nota.fiscalEstado === 'NO_EMITIDA' ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={notaFiscalId === nota.id}
                        onClick={() => void emitirNota(nota.id)}
                      >
                        {notaFiscalId === nota.id ? 'Emitiendo…' : 'Fiscal'}
                      </button>
                    ) : null}
                    {nota.fiscalUrl ? (
                      <a
                        className="btn btn--ghost btn--sm"
                        href={nota.fiscalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver nota
                      </a>
                    ) : null}
                    {nota.fiscalEstado !== 'NO_EMITIDA' && !nota.fiscalUrl ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => void descargarNotaXml(nota.id, nota.numero)}
                      >
                        XML
                      </button>
                    ) : null}
                    {canAnnul && nota.estado === 'emitida' ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => onAnularNota({ id: nota.id, numero: nota.numero })}
                      >
                        Anular
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <p className="page-footnote">
          Tasa aplicada: {formatRate(factura.tasa)} VES/USD · Correlativo fiscal {factura.numero}
        </p>
      </div>
    </Modal>
  );
}

function NotaFormModal({
  tipo,
  factura,
  onClose,
  onCreated,
}: {
  tipo: 'CREDITO' | 'DEBITO';
  factura: FacturaDetalle;
  onClose: () => void;
  onCreated: (numero: string) => void;
}) {
  const { notify } = useToast();
  const crear = useCrearNota();
  const esCredito = tipo === 'CREDITO';
  const [modalidad, setModalidad] = useState<NotaModalidad>(
    esCredito ? 'DEVOLUCION' : 'CARGO',
  );
  const [fecha, setFecha] = useState(today);
  const [motivo, setMotivo] = useState('');
  const [devolverInventario, setDevolverInventario] = useState(true);
  const [emitirFiscal, setEmitirFiscal] = useState(true);
  const [seleccion, setSeleccion] = useState<
    Record<string, { checked: boolean; cantidad: string; precio: string }>
  >({});
  const [manual, setManual] = useState({
    descripcion: '',
    cantidad: '1',
    precio: '',
    exenta: false,
  });

  const esAjuste = modalidad === 'AJUSTE_PRECIO';
  const esDevolucion = modalidad === 'DEVOLUCION';
  const precioFacturado = (item: FacturaItem) =>
    item.precioUnitarioVes > 0
      ? item.precioUnitarioVes
      : item.precioUnitarioUsd * (factura.tasa || 1);

  const lineas = factura.items
    .map((item) => {
      const disponible = Math.max(0, item.cantidad - (item.devuelto ?? 0));
      return { item, disponible };
    })
    .filter(({ disponible }) => (esCredito && esDevolucion ? disponible > 0.0001 : true));

  const preview = useMemo(() => {
    let base = 0;
    let iva = 0;
    const rate = factura.tasa || 1;
    const redondear = (value: number) => Math.round(value * 100) / 100;
    const baseVes = (item: FacturaItem) =>
      item.precioUnitarioVes > 0 ? item.precioUnitarioVes : item.precioUnitarioUsd * rate;

    for (const item of factura.items) {
      const disponible = Math.max(0, item.cantidad - (item.devuelto ?? 0));
      const selected = seleccion[item.id];
      if (!selected?.checked) continue;
      const maximo = esCredito && esDevolucion ? disponible : item.cantidad;
      const qty = Math.min(Number(selected.cantidad) || 0, maximo);
      if (!(qty > 0)) continue;
      let lineaBase: number;
      if (esAjuste) {
        const original = baseVes(item);
        const nuevo = Number(selected.precio) || 0;
        const delta = esCredito ? original - nuevo : nuevo - original;
        if (!(delta > 0)) continue;
        lineaBase = qty * delta;
      } else {
        lineaBase = qty * baseVes(item);
      }
      base += lineaBase;
      iva += item.exenta ? 0 : lineaBase * (item.impuestoPct / 100);
    }
    if (modalidad === 'CARGO' && manual.descripcion.trim() && Number(manual.precio) > 0) {
      const lineaBase = Number(manual.cantidad || 0) * Number(manual.precio);
      base += lineaBase;
      iva += manual.exenta ? 0 : lineaBase * 0.16;
    }
    return { base: redondear(base), iva: redondear(iva), total: redondear(base + iva) };
  }, [factura.items, factura.tasa, seleccion, manual, esCredito, esDevolucion, esAjuste, modalidad]);
  const total = preview.total;

  const submit = async () => {
    if (motivo.trim().length < 5) {
      notify('El motivo debe tener al menos 5 caracteres.', 'warning');
      return;
    }
    const items: Array<Record<string, unknown>> = [];
    for (const { item, disponible } of lineas) {
      const selected = seleccion[item.id];
      if (!selected?.checked) continue;
      const maximo = esCredito && esDevolucion ? disponible : item.cantidad;
      const cantidad = Math.min(Number(selected.cantidad) || 0, maximo);
      if (!(cantidad > 0)) continue;
      if (esAjuste) {
        const original = precioFacturado(item);
        const nuevo = Number(selected.precio) || 0;
        const delta = esCredito ? original - nuevo : nuevo - original;
        if (!(delta > 0)) {
          notify(
            esCredito
              ? `El precio ajustado de "${item.descripcion}" debe ser menor al facturado.`
              : `El precio ajustado de "${item.descripcion}" debe ser mayor al facturado.`,
            'warning',
          );
          return;
        }
        items.push({
          facturaItemId: item.id,
          cantidad,
          precioAjustadoVes: nuevo,
        });
      } else {
        items.push({ facturaItemId: item.id, cantidad });
      }
    }
    if (modalidad === 'CARGO' && manual.descripcion.trim() && Number(manual.precio) > 0) {
      items.push({
        descripcion: manual.descripcion.trim(),
        cantidad: Number(manual.cantidad) || 1,
        precioUnitarioUsd: Number(manual.precio) / (factura.tasa || 1),
        exenta: manual.exenta,
        impuestoPct: manual.exenta ? 0 : 16,
      });
    }
    if (items.length === 0) {
      notify('Selecciona al menos una línea para la nota.', 'warning');
      return;
    }
    try {
      const result = await crear.mutateAsync({
        tipo,
        modalidad,
        facturaId: factura.id,
        fecha,
        motivo: motivo.trim(),
        devolverInventario: esCredito && esDevolucion ? devolverInventario : false,
        emitirFiscal,
        idempotencyKey: crypto.randomUUID(),
        items,
      });
      const data = result.data;
      if (data.fiscalError) {
        notify(
          `Nota ${data.numero} creada, pero el comprobante fiscal no se emitió: ${data.fiscalError}`,
          'warning',
        );
      } else if (data.fiscal?.numeroControl) {
        notify(
          `Nota ${data.numero} emitida · comprobante ${data.fiscal.numeroControl}.`,
          'success',
        );
      } else {
        notify(`Nota ${data.numero} creada.`, 'success');
      }
      onCreated(data.numero);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo emitir la nota.', 'error');
    }
  };

  const notaTitulo = esCredito ? 'Nota de crédito' : 'Nota de débito';
  const explicacion = esAjuste
    ? esCredito
      ? 'Ajuste de precio: revierte solo la diferencia entre el precio facturado y el nuevo precio. Afecta ingreso, IVA y CxC; no mueve inventario.'
      : 'Ajuste de precio: carga la diferencia entre el precio facturado y el nuevo precio. Aumenta ingreso, IVA y CxC; no mueve inventario.'
    : esDevolucion
      ? 'Devolución: revierte cantidad, ingreso, IVA y CxC. Si reingresas la mercancía también se reversa el costo de venta.'
      : 'Cargo adicional: aumenta CxC, ingreso e IVA sin mover inventario. Úsalo para fletes o servicios adicionales.';

  return (
    <Modal
      title={notaTitulo}
      eyebrow={`${factura.numero} · ${factura.clienteNombre}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>
            {crear.isPending ? 'Emitiendo…' : `Emitir nota · ${formatMoney(total, 'VES')}`}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="segmented segmented--solid" role="group" aria-label="Modalidad de la nota">
          {esCredito ? (
            <button
              type="button"
              className={esDevolucion ? 'is-active' : ''}
              onClick={() => setModalidad('DEVOLUCION')}
            >
              Devolución
            </button>
          ) : (
            <button
              type="button"
              className={modalidad === 'CARGO' ? 'is-active' : ''}
              onClick={() => setModalidad('CARGO')}
            >
              Cargo adicional
            </button>
          )}
          <button
            type="button"
            className={esAjuste ? 'is-active' : ''}
            onClick={() => setModalidad('AJUSTE_PRECIO')}
          >
            Ajuste de precio
          </button>
        </div>

        <p className="inline-note">{explicacion}</p>

        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Fecha</span>
            <input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <label className="field field--wide">
            <span className="field-label">Motivo *</span>
            <input
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Devolución parcial, ajuste de precio, cargo adicional…"
            />
          </label>
        </div>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Líneas de la factura</strong>
            <span className="pill pill--soft">
              {esAjuste ? 'Ajuste de precio' : esDevolucion ? 'Devolución' : 'Cargo'}
            </span>
          </div>
          <div className="table-scroll">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th />
                  <th>Descripción</th>
                  <th className="align-right">Facturado</th>
                  <th className="align-right">
                    {esAjuste ? 'Precio facturado' : 'Disponible'}
                  </th>
                  <th className="align-right">Cantidad</th>
                  <th className="align-right">{esAjuste ? 'Precio ajustado' : 'Precio'}</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map(({ item, disponible }) => {
                  const state =
                    seleccion[item.id] ?? {
                      checked: false,
                      cantidad: String(disponible || item.cantidad),
                      precio: String(precioFacturado(item)),
                    };
                  return (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={state.checked}
                          onChange={(e) =>
                            setSeleccion((current) => ({
                              ...current,
                              [item.id]: { ...state, checked: e.target.checked },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <div className="doc-cell">
                          <strong>{item.descripcion}</strong>
                          <span>{item.codigo ?? (item.exenta ? 'Exenta' : `IVA ${item.impuestoPct}%`)}</span>
                        </div>
                      </td>
                      <td className="align-right">{item.cantidad}</td>
                      <td className="align-right">
                        {esAjuste
                          ? formatMoney(precioFacturado(item), 'VES')
                          : disponible}
                      </td>
                      <td className="align-right">
                        <input
                          className="input input--qty"
                          type="number"
                          min="0.001"
                          max={esCredito && esDevolucion ? disponible : item.cantidad}
                          step="0.001"
                          value={state.cantidad}
                          disabled={!state.checked}
                          onChange={(e) =>
                            setSeleccion((current) => ({
                              ...current,
                              [item.id]: { ...state, cantidad: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td className="align-right">
                        {esAjuste ? (
                          <input
                            className="input input--qty"
                            type="number"
                            min="0"
                            step="0.01"
                            value={state.precio}
                            disabled={!state.checked}
                            onChange={(e) =>
                              setSeleccion((current) => ({
                                ...current,
                                [item.id]: { ...state, precio: e.target.value },
                              }))
                            }
                            aria-label={`Precio ajustado de ${item.descripcion}`}
                          />
                        ) : (
                          formatMoney(precioFacturado(item), 'VES')
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {esAjuste ? (
            <p className="page-footnote">
              Escribe el nuevo precio unitario en bolívares. Debe ser{' '}
              {esCredito ? 'menor' : 'mayor'} al precio facturado; la nota solo registra la
              diferencia y no mueve inventario.
            </p>
          ) : null}
          {esCredito && esDevolucion && factura.tipo === 'ARTICULO' ? (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={devolverInventario}
                onChange={(e) => setDevolverInventario(e.target.checked)}
              />
              Reingresar al inventario y reversar costo de venta
            </label>
          ) : null}
        </div>

        {modalidad === 'CARGO' ? (
          <div className="line-items">
            <div className="line-items__head"><strong>Concepto manual adicional</strong></div>
            <div className="form-grid form-grid--3">
              <label className="field field--wide">
                <span className="field-label">Descripción</span>
                <input
                  className="input"
                  value={manual.descripcion}
                  onChange={(e) => setManual({ ...manual, descripcion: e.target.value })}
                  placeholder="Flete, ajuste, servicio adicional…"
                />
              </label>
              <label className="field">
                <span className="field-label">Cantidad</span>
                <input className="input" type="number" min="0.001" value={manual.cantidad} onChange={(e) => setManual({ ...manual, cantidad: e.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Precio Bs.</span>
                <input className="input" type="number" min="0" value={manual.precio} onChange={(e) => setManual({ ...manual, precio: e.target.value })} />
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={manual.exenta} onChange={(e) => setManual({ ...manual, exenta: e.target.checked })} />
                Exenta de IVA
              </label>
            </div>
          </div>
        ) : null}

        <div className="note-totals">
          <div><span>Base imponible</span><strong>{formatMoney(preview.base, 'VES')}</strong></div>
          <div><span>IVA</span><strong>{formatMoney(preview.iva, 'VES')}</strong></div>
          <div className="note-totals__grand"><span>Total de la nota</span><strong>{formatMoney(preview.total, 'VES')}</strong></div>
        </div>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={emitirFiscal}
            onChange={(e) => setEmitirFiscal(e.target.checked)}
          />
          Emitir el comprobante fiscal automáticamente (TFHKA / contingencia local)
        </label>
        <p className="page-footnote">
          El asiento se genera al guardar la nota con la tasa original de la factura. El
          comprobante fiscal se emite en el mismo paso y queda enlazado a la factura{' '}
          {factura.numero}.
        </p>
      </div>
    </Modal>
  );
}
